import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { useNavigate } from 'react-router-dom';
import { Loader2, Folder, FileText, CheckCircle2, ShieldAlert, Clock, PauseCircle, Ban, ExternalLink, XCircle, Copy, PlayCircle, IndianRupee, Send, Mail, FileCheck, TrendingUp } from 'lucide-react';
import { IdeasFilterTable } from '../components/IdeasFilterTable.tsx';
import { AmountDisplay } from '../components/AmountDisplay.tsx';

export function CommitteeMemberDashboard({ showFuturePotentialByEc = true }: { showFuturePotentialByEc?: boolean } = {}) {
  const navigate = useNavigate();
  const userSubCoreRole = localStorage.getItem('userSubCoreRole') || '';
  const isExecutor = userSubCoreRole === 'EXECUTOR';

  const { data: globalStats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/stats');
      return res.data.data;
    },
  });

  return (
    <Layout title={isExecutor ? "Executor Committee Portal" : "Committee Member Portal"}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-800">DOM680 Dashboard</h2>
          <p className="text-slate-500 text-sm">Review, prioritize and moderate idea workflow</p>
        </div>
        {!isExecutor && (
          <button
            onClick={() => navigate('/admin/bulk-evaluate-import')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3.5 py-1.5 rounded-lg text-xs transition-all flex items-center justify-center space-x-1.5 cursor-pointer shadow-sm border-0 self-start sm:self-auto"
          >
            <FileCheck className="w-4 h-4" />
            <span>Bulk Evaluation Import</span>
          </button>
        )}
      </div>

      {statsLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : isExecutor ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
                <Folder className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-600 font-medium">Total Ideas</p>
            </div>
            <p className="text-2xl font-bold text-slate-800">{globalStats?.kpis?.totalIdeas || 0}</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
            <div className="flex items-center space-x-3">
              <div className="bg-orange-50 p-2 rounded-lg text-orange-600">
                <ExternalLink className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-600 font-medium">Requested EC Head Approval</p>
            </div>
            <p className="text-2xl font-bold text-slate-800">{globalStats?.kpis?.pendingExternalApproval || 0}</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
            <div className="flex items-center space-x-3">
              <div className="bg-emerald-50 p-2 rounded-lg text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-600 font-medium">External Approved</p>
            </div>
            <p className="text-2xl font-bold text-slate-800">{globalStats?.kpis?.approved || 0}</p>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
            <div className="flex items-center space-x-3">
              <div className="bg-rose-50 p-2 rounded-lg text-rose-600">
                <XCircle className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-600 font-medium">External Rejected</p>
            </div>
            <p className="text-2xl font-bold text-slate-800">{globalStats?.kpis?.externalRejected || 0}</p>
          </div>
          {/* <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
            <div className="flex items-center space-x-3">
              <div className="bg-red-50 p-2 rounded-lg text-red-600">
                <Ban className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-600 font-medium">Rejected</p>
            </div>
            <p className="text-2xl font-bold text-slate-800">{globalStats?.kpis?.rejected || 0}</p>
          </div> */}

          {/* <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
            <div className="flex items-center space-x-3">
              <div className="bg-slate-100 p-2 rounded-lg text-slate-600">
                <Copy className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-600 font-medium">Duplicate</p>
            </div>
            <p className="text-2xl font-bold text-slate-800">{globalStats?.kpis?.duplicateCount || 0}</p>
          </div> */}

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
            <div className="flex items-center space-x-3">
              <div className="bg-green-50 p-2 rounded-lg text-green-600">
                <IndianRupee className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-600 font-medium">Internal Committee Head Approved</p>
            </div>
            <AmountDisplay
              amount={globalStats?.kpis?.totalAnnualSavings || 0}
              label="Internal Committee Head Approved"
            />
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-50 p-2 rounded-lg text-purple-600">
                <IndianRupee className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-600 font-medium">External Committee Approved</p>
            </div>
            <AmountDisplay
              amount={globalStats?.kpis?.actualExternalApprovedAmount || 0}
              label="External Committee Approved"
            />
          </div>
        </div>
      ) : (
        <div className="mb-8">
          {/* Row 1: 6 simple status cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 mb-4">
            {/* 1) Total Ideas */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-blue-50 p-1.5 rounded-lg text-blue-600 shrink-0">
                  <Folder className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Total Ideas</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.totalIdeas || 0}</p>
            </div>

            {/* 2) Hold */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-amber-50 p-1.5 rounded-lg text-amber-600 shrink-0">
                  <ShieldAlert className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Hold</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.infoRequested || 0}</p>
            </div>

            {/* 3) Duplicate */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-slate-100 p-1.5 rounded-lg text-slate-600 shrink-0">
                  <Copy className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Duplicate</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.duplicateCount || 0}</p>
            </div>

            {/* 4) Rejected */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-red-50 p-1.5 rounded-lg text-red-600 shrink-0">
                  <Ban className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Rejected</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.rejected || 0}</p>
            </div>



            {/* 5) Reviewed */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-orange-50 p-1.5 rounded-lg text-orange-600 shrink-0">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Reviewed</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.underEvaluation || 0}</p>
            </div>

            {/* 6) Yet to Be Reviewed */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 mr-1">
                <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600 shrink-0">
                  <Send className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">Yet to Be Reviewed</p>
              </div>
              <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.submitted || 0}</p>
            </div>
          </div>

          {/* Row 2: Nos & Value cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {/* 1) WIP */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-cyan-50 p-1.5 rounded-lg text-cyan-600 shrink-0">
                  <PlayCircle className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate">WIP</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.workInProgressCount || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.wipTotalAmount || 0} />
                </div>
              </div>
            </div>

            {/* 2) Pending IC Head Approval */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-emerald-50 p-1.5 rounded-lg text-emerald-600 shrink-0">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Pending IC Head Approval">Pending IC Head Approval</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.pendingHeadApproval || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.pendingHeadApprovalValue || 0} />
                </div>
              </div>
            </div>

            {/* 3) Requested EC Head Approval */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-fuchsia-50 p-1.5 rounded-lg text-fuchsia-600 shrink-0">
                  <ExternalLink className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate" title="Requested EC Head Approval">Requested EC Head Approval</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.pendingExternalApproval || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.pendingExternalApprovalValue || 0} />
                </div>
              </div>
            </div>

            {/* 3) EC Head Approved */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-green-50 p-1.5 rounded-lg text-green-600 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate" title="EC Head Approved">EC Head Approved</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.approved || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.actualExternalApprovedAmount || 0} />
                </div>
              </div>
            </div>

            {showFuturePotentialByEc && (
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-amber-50 p-1.5 rounded-lg text-amber-600 shrink-0">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate" title="Future potential by EC">Future potential by EC</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">
                    {/* OLD: Requested − (Approved + Email Confirmed)
                    {(globalStats?.kpis?.pendingExternalApproval || 0) -
                      ((globalStats?.kpis?.approved || 0) + (globalStats?.kpis?.emailConfirmedCount || 0))}
                    */}
                    {globalStats?.kpis?.futurePotentialByEcCount || 0}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay
                    amount={
                      /* OLD: Requested − (Approved + Email Confirmed)
                      (globalStats?.kpis?.pendingExternalApprovalValue || 0) -
                      ((globalStats?.kpis?.actualExternalApprovedAmount || 0) +
                        (globalStats?.kpis?.emailConfirmationAnnualSavings || 0))
                      */
                      globalStats?.kpis?.futurePotentialByEcValue || 0
                    }
                    forceCrores={true}
                  />
                </div>
              </div>
            </div>
            )}

            {/* 4) EC Head Rejected */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-rose-50 p-1.5 rounded-lg text-rose-600 shrink-0">
                  <XCircle className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate" title="EC Head Rejected">EC Head Rejected</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.externalRejected || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.externalRejectedValue || 0} />
                </div>
              </div>
            </div>

            {/* 5) EC Head Email Confirmed */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
              <div className="flex items-center space-x-2.5 mb-2.5">
                <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600 shrink-0">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold truncate" title="EC Head Email Confirmed">EC Head Email Confirmed</p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.emailConfirmedCount || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.emailConfirmationAnnualSavings || 0} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <IdeasFilterTable
        queryKeyPrefix="memberIdeas"
        apiEndpoint="/admin/ideas"
        showExport={true}
        showFuturePotentialByEc={showFuturePotentialByEc}
        allowDelete={false}
        actionButtonLabel="Evaluate"
      />
    </Layout >
  );
}
